import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../contexts/AuthContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, User, Mail, Loader2, Pencil, Trash2, Building2, KeyRound,
  Search, Download, Upload, Sparkles, Filter, Users as UsersIcon,
  ShieldCheck, X, Check, UserX, UserCheck, ShieldAlert, History
} from 'lucide-react';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  hotel_manager: 'Hotel Manager',
  capo_manutentore: 'Capo Manutentore',
  technician: 'Tecnico',
  housekeeping: 'Housekeeping',
  frontoffice: 'Front Office',
  sede: 'Sede',
};

const ROLE_OPTIONS = Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }));

const DEACTIVATION_REASONS = [
  { value: 'dimissioni', label: 'Dimissioni' },
  { value: 'licenziamento', label: 'Licenziamento' },
  { value: 'trasferimento', label: 'Trasferimento' },
  { value: 'fine_contratto', label: 'Fine contratto' },
  { value: 'altro', label: 'Altro' },
];

const ROLE_BADGE = {
  super_admin: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  hotel_manager: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  capo_manutentore: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  technician: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  housekeeping: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  frontoffice: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  sede: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
};

const PASSWORD_RULES = [
  { key: 'len', label: 'Almeno 8 caratteri', test: (p) => p.length >= 8 },
  { key: 'upper', label: 'Una maiuscola', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'Una minuscola', test: (p) => /[a-z]/.test(p) },
  { key: 'digit', label: 'Un numero', test: (p) => /\d/.test(p) },
  { key: 'special', label: 'Un simbolo (!@#$%...)', test: (p) => /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(p) },
];

const EMPTY_FORM = { name: '', email: '', password: '', role: 'technician', assigned_hotels: [] };

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterTheme, setFilterTheme] = useState('all');
  const [filterHotel, setFilterHotel] = useState('all');

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Dialogs
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [resetPassword, setResetPasswordVal] = useState('');
  const [resettingPwd, setResettingPwd] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  // Soft-delete lifecycle (ex dipendenti)
  const [showInactive, setShowInactive] = useState(false);
  const [deactivateUser, setDeactivateUser] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState('dimissioni');
  const [deactivateNotes, setDeactivateNotes] = useState('');
  const [deactivating, setDeactivating] = useState(false);
  const [anonymizeUser, setAnonymizeUser] = useState(null);
  const [anonymizeConfirmation, setAnonymizeConfirmation] = useState('');
  const [anonymizing, setAnonymizing] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, hotelsRes] = await Promise.all([
        api.get(`/users${showInactive ? '?include_inactive=true' : ''}`),
        api.get('/hotels'),
      ]);
      setUsers(usersRes.data);
      setHotels(hotelsRes.data);
    } catch (err) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [showInactive]);

  const hotelById = useMemo(
    () => Object.fromEntries(hotels.map((h) => [h.id, h.name])),
    [hotels]
  );

  // ---- Derived: filtered users + stats ----
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (filterRole !== 'all' && u.role !== filterRole) return false;
      if (filterTheme !== 'all' && (u.ui_theme || 'default') !== filterTheme) return false;
      if (filterHotel !== 'all' && !(u.assigned_hotels || []).includes(filterHotel)) return false;
      if (q && !(u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [users, search, filterRole, filterTheme, filterHotel]);

  const stats = useMemo(() => {
    const byRole = {};
    let withDataIntel = 0;
    users.forEach((u) => {
      byRole[u.role] = (byRole[u.role] || 0) + 1;
      if (u.ui_theme === 'data-intel') withDataIntel++;
    });
    return { total: users.length, byRole, withDataIntel };
  }, [users]);

  // ---- Selection ----
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => {
    const next = new Set(filtered.map((u) => u.id));
    setSelectedIds(next);
  };
  const clearSelection = () => setSelectedIds(new Set());

  // ---- UI theme actions ----
  const setUserTheme = async (userId, theme) => {
    try {
      await api.patch(`/users/${userId}/ui-theme`, { ui_theme: theme });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, ui_theme: theme } : u)));
      toast.success(`Tema "${theme === 'data-intel' ? 'Data Intel' : 'Classico'}" applicato`);
    } catch (err) {
      toast.error('Errore nel cambio tema');
    }
  };

  const bulkSetTheme = async (theme) => {
    if (selectedIds.size === 0) return;
    try {
      await api.post('/users/ui-theme/bulk', { ui_theme: theme, user_ids: Array.from(selectedIds) });
      toast.success(`Tema applicato a ${selectedIds.size} utenti`);
      await fetchData();
      clearSelection();
    } catch (err) {
      toast.error('Errore nel cambio tema in blocco');
    }
  };

  const applyThemeToAll = async (theme) => {
    if (!window.confirm(`Sei sicuro di applicare il tema "${theme === 'data-intel' ? 'Data Intel' : 'Classico'}" a TUTTI gli utenti?`)) return;
    try {
      const { data } = await api.post('/users/ui-theme/bulk', { ui_theme: theme, all: true });
      toast.success(`Tema applicato a ${data.modified} utenti`);
      await fetchData();
      clearSelection();
    } catch (err) {
      toast.error('Errore nell\'applicazione globale del tema');
    }
  };

  // ---- CRUD ----
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editUser) {
        const updateData = { name: formData.name, role: formData.role, assigned_hotels: formData.assigned_hotels };
        await api.put(`/users/${editUser.id}`, updateData);
        toast.success('Utente aggiornato');
      } else {
        await api.post('/users', formData);
        toast.success('Utente creato');
      }
      setCreateOpen(false);
      setEditUser(null);
      setFormData(EMPTY_FORM);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Errore nel salvataggio';
      toast.error(typeof msg === 'string' ? msg : 'Errore nel salvataggio');
    }
  };

  const handleEdit = (u) => {
    setEditUser(u);
    setFormData({ name: u.name, email: u.email, password: '', role: u.role, assigned_hotels: u.assigned_hotels || [] });
    setCreateOpen(true);
  };

  const handleDelete = async (u) => {
    // Deprecated shortcut: apre il dialog di disattivazione strutturata
    openDeactivateDialog(u);
  };

  const openDeactivateDialog = (u) => {
    setDeactivateUser(u);
    setDeactivateReason('dimissioni');
    setDeactivateNotes('');
  };

  const submitDeactivate = async () => {
    if (!deactivateUser) return;
    try {
      setDeactivating(true);
      await api.post(`/users/${deactivateUser.id}/deactivate`, {
        reason: deactivateReason,
        notes: deactivateNotes,
      });
      toast.success(`${deactivateUser.name} disattivato — storico preservato.`);
      setDeactivateUser(null);
      setDeactivateNotes('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore disattivazione');
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async (u) => {
    if (!window.confirm(`Riattivare ${u.name} (${u.email})?\n\nPotrà nuovamente accedere ed essere assegnato a ticket.`)) return;
    try {
      await api.post(`/users/${u.id}/reactivate`);
      toast.success(`${u.name} riattivato.`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore riattivazione');
    }
  };

  const openAnonymizeDialog = (u) => {
    setAnonymizeUser(u);
    setAnonymizeConfirmation('');
  };

  const submitAnonymize = async () => {
    if (!anonymizeUser) return;
    if (anonymizeConfirmation !== 'ANONIMIZZA') {
      toast.error('Digita esattamente ANONIMIZZA per confermare');
      return;
    }
    try {
      setAnonymizing(true);
      await api.post(`/users/${anonymizeUser.id}/anonymize`, { confirmation: 'ANONIMIZZA' });
      toast.success(`${anonymizeUser.name} anonimizzato (GDPR).`);
      setAnonymizeUser(null);
      setAnonymizeConfirmation('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore anonimizzazione');
    } finally {
      setAnonymizing(false);
    }
  };

  const pwdChecks = PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(resetPassword) }));
  const pwdValid = pwdChecks.every((c) => c.ok);

  const handleResetPassword = async () => {
    if (!pwdValid) {
      toast.error('La password non rispetta tutti i requisiti');
      return;
    }
    try {
      setResettingPwd(true);
      await api.post(`/users/${resetPasswordUser.id}/reset-password`, { new_password: resetPassword });
      toast.success(`Password resettata per ${resetPasswordUser.email} (account sbloccato)`);
      setResetPasswordUser(null);
      setResetPasswordVal('');
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Errore nel reset password');
    } finally {
      setResettingPwd(false);
    }
  };

  const toggleHotel = (hotelId) => {
    setFormData((prev) => ({
      ...prev,
      assigned_hotels: prev.assigned_hotels.includes(hotelId)
        ? prev.assigned_hotels.filter((id) => id !== hotelId)
        : [...prev.assigned_hotels, hotelId],
    }));
  };

  // ---- CSV Import / Export ----
  const exportCSV = () => {
    const headers = ['email', 'name', 'role', 'assigned_hotels', 'ui_theme', 'is_active', 'created_at'];
    const rows = users.map((u) => [
      u.email,
      u.name,
      u.role,
      (u.assigned_hotels || []).map((id) => hotelById[id] || id).join('|'),
      u.ui_theme || 'default',
      u.is_active !== false ? 'true' : 'false',
      u.created_at || '',
    ]);
    const csv = [headers, ...rows].map((row) => row.map((cell) => {
      const s = String(cell ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hostfix-utenti-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${users.length} utenti esportati`);
  };

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
    const idx = (k) => headers.indexOf(k);
    return lines.slice(1).map((line) => {
      // Simple CSV parser (no embedded commas in quoted fields support for brevity)
      const cells = line.split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
      return {
        email: cells[idx('email')],
        name: cells[idx('name')],
        role: cells[idx('role')] || 'technician',
        password: cells[idx('password')] || 'TempPass1!',
      };
    }).filter((r) => r.email && r.name);
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (!rows.length) { toast.error('CSV vuoto o malformato'); return; }
      if (!window.confirm(`Importare ${rows.length} utenti dal CSV?\n(richiede colonne: email, name, role, password)`)) return;

      let ok = 0, ko = 0;
      for (const r of rows) {
        try {
          await api.post('/users', { ...r, assigned_hotels: [] });
          ok++;
        } catch {
          ko++;
        }
      }
      toast.success(`Import: ${ok} creati · ${ko} errori`);
      fetchData();
    } catch (err) {
      toast.error('Errore lettura CSV');
    } finally {
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="users-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const allFilteredSelected = filtered.length > 0 && filtered.every((u) => selectedIds.has(u.id));

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header + KPI */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono">
              Gestione Utenti
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Utenti</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {stats.total} totali · {stats.withDataIntel} con tema Data Intel · {filtered.length} visibili
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="file"
              accept=".csv"
              id="csv-import"
              style={{ display: 'none' }}
              onChange={handleImportCSV}
              data-testid="csv-import-input"
            />
            <Button variant="outline" size="sm" onClick={() => document.getElementById('csv-import').click()} data-testid="import-csv-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Importa CSV
            </Button>
            <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1.5" /> Esporta CSV
            </Button>
            <Dialog open={createOpen} onOpenChange={(open) => {
              setCreateOpen(open);
              if (!open) { setEditUser(null); setFormData(EMPTY_FORM); }
            }}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="create-user-button">
                  <Plus className="w-4 h-4" /> Nuovo Utente
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editUser ? 'Modifica Utente' : 'Nuovo Utente'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Nome *</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Mario Rossi" required data-testid="user-name-input" />
                  </div>
                  {!editUser && (
                    <>
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="mario.rossi@investhosp.com" required data-testid="user-email-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Password *</Label>
                        <Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} placeholder="Min. 8 caratteri, maiuscola, numero, simbolo" required data-testid="user-password-input" />
                      </div>
                    </>
                  )}
                  <div className="space-y-2">
                    <Label>Ruolo *</Label>
                    <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                      <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {['hotel_manager', 'capo_manutentore', 'technician', 'housekeeping', 'frontoffice'].includes(formData.role) && (
                    <div className="space-y-2">
                      <Label>Hotel Assegnati</Label>
                      <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-1">
                        {hotels.map((hotel) => (
                          <label key={hotel.id} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-muted rounded text-sm">
                            <input
                              type="checkbox"
                              checked={formData.assigned_hotels.includes(hotel.id)}
                              onChange={() => toggleHotel(hotel.id)}
                            />
                            <span>{hotel.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
                    <Button type="submit" data-testid="user-submit-button">{editUser ? 'Salva' : 'Crea Utente'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Totali</span>
                <UsersIcon className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="text-3xl font-bold mt-1">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Admin / Manager</span>
                <ShieldCheck className="w-4 h-4 text-red-500" />
              </div>
              <div className="text-3xl font-bold mt-1">{(stats.byRole.super_admin || 0) + (stats.byRole.hotel_manager || 0)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Operativi</span>
                <Building2 className="w-4 h-4 text-cyan-500" />
              </div>
              <div className="text-3xl font-bold mt-1">
                {(stats.byRole.capo_manutentore || 0) + (stats.byRole.technician || 0) + (stats.byRole.housekeeping || 0) + (stats.byRole.frontoffice || 0)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Tema Data Intel</span>
                <Sparkles className="w-4 h-4 text-blue-500" />
              </div>
              <div className="text-3xl font-bold mt-1">
                {stats.withDataIntel}<span className="text-base font-normal text-muted-foreground">/{stats.total}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Filter strip */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca nome o email..."
            className="pl-8"
            data-testid="users-search"
          />
        </div>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-[170px]" data-testid="filter-role">
            <Filter className="w-3.5 h-3.5 mr-1.5" />
            <SelectValue placeholder="Ruolo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i ruoli</SelectItem>
            {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterTheme} onValueChange={setFilterTheme}>
          <SelectTrigger className="w-[170px]" data-testid="filter-theme">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            <SelectValue placeholder="Tema" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i temi</SelectItem>
            <SelectItem value="default">Classico</SelectItem>
            <SelectItem value="data-intel">Data Intel</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterHotel} onValueChange={setFilterHotel}>
          <SelectTrigger className="w-[200px]" data-testid="filter-hotel">
            <Building2 className="w-3.5 h-3.5 mr-1.5" />
            <SelectValue placeholder="Hotel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli hotel</SelectItem>
            {hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {(search || filterRole !== 'all' || filterTheme !== 'all' || filterHotel !== 'all') && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setFilterRole('all'); setFilterTheme('all'); setFilterHotel('all'); }} data-testid="clear-filters">
            <X className="w-4 h-4 mr-1" /> Pulisci
          </Button>
        )}
        <div className="flex-1" />
        <Button
          variant={showInactive ? 'default' : 'outline'}
          size="sm"
          onClick={() => setShowInactive((v) => !v)}
          className={showInactive ? 'bg-slate-700 hover:bg-slate-800 text-white' : ''}
          data-testid="toggle-inactive-users"
        >
          <History className="w-4 h-4 mr-1.5" />
          {showInactive ? 'Nascondi ex dipendenti' : 'Mostra ex dipendenti'}
        </Button>
      </div>

      {/* Bulk toolbar — solo se ci sono selezioni */}
      {selectedIds.size > 0 && (
        <Card className="border-primary/30 bg-primary/5" data-testid="bulk-toolbar">
          <CardContent className="p-3 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium">{selectedIds.size} selezionati</span>
            <div className="h-4 w-px bg-border" />
            <Button size="sm" variant="outline" onClick={() => bulkSetTheme('data-intel')} data-testid="bulk-apply-data-intel">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Applica tema Data Intel
            </Button>
            <Button size="sm" variant="outline" onClick={() => bulkSetTheme('default')} data-testid="bulk-apply-default">
              Applica tema Classico
            </Button>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" onClick={clearSelection}>
              <X className="w-3.5 h-3.5 mr-1" /> Deseleziona
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Quick global actions */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>Azioni rapide:</span>
        <button className="underline underline-offset-2 hover:text-foreground" onClick={() => applyThemeToAll('data-intel')} data-testid="apply-all-data-intel">
          Attiva Data Intel per tutti
        </button>
        <span>·</span>
        <button className="underline underline-offset-2 hover:text-foreground" onClick={() => applyThemeToAll('default')} data-testid="apply-all-default">
          Ripristina tema classico per tutti
        </button>
      </div>

      {/* Users table */}
      <Card>
        <CardContent className="p-0">
          {/* Header row */}
          <div className="grid items-center px-4 py-2.5 border-b text-[10px] uppercase tracking-wider font-mono text-muted-foreground"
            style={{ gridTemplateColumns: '32px 1fr 140px 60px 110px 120px' }}>
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={() => allFilteredSelected ? clearSelection() : selectAllVisible()}
              data-testid="select-all"
            />
            <span>Utente</span>
            <span>Ruolo</span>
            <span className="text-center">Hotel</span>
            <span className="text-center">Tema Data Intel</span>
            <span className="text-right">Azioni</span>
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12">
              <User className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
              <p className="text-muted-foreground text-sm">Nessun utente corrisponde ai filtri</p>
            </div>
          )}

          {filtered.map((u, i) => {
            const isSelected = selectedIds.has(u.id);
            const isMe = currentUser?.id === u.id;
            const isDataIntel = (u.ui_theme || 'default') === 'data-intel';
            const isInactive = u.is_active === false;
            const isAnonymized = !!u.anonymized_at;
            return (
              <div
                key={u.id}
                data-testid={`user-row-${u.id}`}
                className={`grid items-center px-4 py-3 text-sm border-b last:border-b-0 hover:bg-muted/40 transition-colors ${isInactive ? 'opacity-70' : ''}`}
                style={{
                  gridTemplateColumns: '32px 1fr 140px 60px 110px 140px',
                  background: isSelected ? 'hsl(var(--primary) / 0.06)' : undefined,
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSelect(u.id)}
                  data-testid={`select-user-${u.id}`}
                  disabled={isInactive}
                />
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold uppercase shrink-0 ${isInactive ? 'bg-slate-200 dark:bg-slate-800 text-slate-500 grayscale' : 'bg-muted'}`}>
                    {u.name?.split(' ').map((p) => p[0]).slice(0, 2).join('')}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate flex items-center gap-1.5">
                      <span className={isInactive ? 'line-through decoration-slate-400' : ''}>{u.name}</span>
                      {isMe && <Badge variant="outline" className="text-[9px] py-0 px-1">tu</Badge>}
                      {isInactive && !isAnonymized && (
                        <Badge className="bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200 text-[9px] py-0 px-1.5" data-testid={`ex-badge-${u.id}`}>
                          Ex • {DEACTIVATION_REASONS.find(r => r.value === u.deactivation_reason)?.label || u.deactivation_reason || '—'}
                        </Badge>
                      )}
                      {isAnonymized && (
                        <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200 text-[9px] py-0 px-1.5" data-testid={`anon-badge-${u.id}`}>
                          GDPR anonimizzato
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                      <Mail className="w-3 h-3 shrink-0" />
                      <span className="truncate">{u.email}</span>
                    </div>
                    {isInactive && u.deactivated_at && !isAnonymized && (
                      <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                        Disattivato il {new Date(u.deactivated_at).toLocaleDateString('it-IT')}
                        {u.deactivation_notes ? ` — ${u.deactivation_notes}` : ''}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Badge className={`${ROLE_BADGE[u.role]} font-medium`}>
                    {ROLE_LABELS[u.role]}
                  </Badge>
                </div>
                <div className="text-center text-xs text-muted-foreground tabular-nums">
                  {u.assigned_hotels?.length || 0}
                </div>
                <div className="flex items-center justify-center gap-2" data-testid={`theme-toggle-${u.id}`}>
                  {!isInactive && (
                    <>
                      <Switch
                        checked={isDataIntel}
                        onCheckedChange={(checked) => setUserTheme(u.id, checked ? 'data-intel' : 'default')}
                      />
                      {isDataIntel && <Sparkles className="w-3.5 h-3.5 text-blue-500" />}
                    </>
                  )}
                </div>
                <div className="flex items-center justify-end gap-1">
                  {isInactive ? (
                    <>
                      {!isAnonymized && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleReactivate(u)}
                          title="Riattiva utente"
                          data-testid={`reactivate-user-${u.id}`}
                          className="text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                        >
                          <UserCheck className="w-4 h-4" />
                        </Button>
                      )}
                      {!isAnonymized && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openAnonymizeDialog(u)}
                          title="Anonimizza (GDPR)"
                          data-testid={`anonymize-user-${u.id}`}
                          className="text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                        >
                          <ShieldAlert className="w-4 h-4" />
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => { setResetPasswordUser(u); setResetPasswordVal(''); }} title="Reset Password" data-testid={`reset-pwd-${u.id}`}>
                        <KeyRound className="w-4 h-4 text-amber-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(u)} data-testid={`edit-user-${u.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDeactivateDialog(u)}
                        title="Disattiva (ex dipendente)"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        data-testid={`deactivate-user-${u.id}`}
                        disabled={isMe}
                      >
                        <UserX className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Reset password dialog */}
      <Dialog open={!!resetPasswordUser} onOpenChange={(open) => { if (!open) setResetPasswordUser(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Imposta una nuova password per <strong>{resetPasswordUser?.name}</strong> ({resetPasswordUser?.email}).
              L&apos;utente riceverà la notifica via email.
            </p>
            <div className="space-y-2">
              <Label>Nuova Password</Label>
              <Input
                type="text"
                value={resetPassword}
                onChange={(e) => setResetPasswordVal(e.target.value)}
                placeholder="Min. 8 caratteri, maiuscola, numero, simbolo"
                data-testid="reset-password-input"
              />
              <ul className="grid grid-cols-2 gap-x-3 gap-y-1 pt-1" data-testid="reset-password-rules">
                {pwdChecks.map((c) => (
                  <li key={c.key} data-testid={`pwd-rule-${c.key}`} data-ok={c.ok}
                    className={`flex items-center gap-1.5 text-xs transition-colors ${c.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                    {c.ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />} {c.label}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">Il reset sblocca anche l&apos;account se bloccato per troppi tentativi.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResetPasswordUser(null)} data-testid="cancel-reset-password">Annulla</Button>
              <Button onClick={handleResetPassword} disabled={!pwdValid || resettingPwd} data-testid="confirm-reset-password">
                {resettingPwd ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                {resettingPwd ? 'Salvataggio...' : 'Reset Password'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deactivate dialog (ex dipendente) */}
      <Dialog open={!!deactivateUser} onOpenChange={(open) => { if (!open) { setDeactivateUser(null); setDeactivateNotes(''); } }}>
        <DialogContent data-testid="deactivate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserX className="w-5 h-5 text-red-600" />
              Disattiva utente
            </DialogTitle>
          </DialogHeader>
          {deactivateUser && (
            <div className="space-y-4 pt-2">
              <div className="text-sm text-muted-foreground">
                Stai per disattivare <b className="text-foreground">{deactivateUser.name}</b> ({deactivateUser.email}).
              </div>
              <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 text-xs text-blue-800 dark:text-blue-200 space-y-1">
                <div>✓ Lo storico ticket, note e cartellini <b>viene preservato</b>.</div>
                <div>✓ L&apos;utente <b>non potrà più loggarsi</b> né essere assegnato a nuovi ticket.</div>
                <div>✓ Potrai <b>riattivarlo in qualsiasi momento</b>.</div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="deactivate-reason">Motivo <span className="text-red-500">*</span></Label>
                <Select value={deactivateReason} onValueChange={setDeactivateReason}>
                  <SelectTrigger id="deactivate-reason" data-testid="deactivate-reason-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEACTIVATION_REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="deactivate-notes">Note (opzionali)</Label>
                <Input
                  id="deactivate-notes"
                  value={deactivateNotes}
                  onChange={(e) => setDeactivateNotes(e.target.value)}
                  placeholder="Es. Ultimo giorno 31/12/2025, subentra Rossi"
                  data-testid="deactivate-notes-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDeactivateUser(null)} disabled={deactivating}>
                  Annulla
                </Button>
                <Button
                  onClick={submitDeactivate}
                  disabled={deactivating}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="confirm-deactivate-button"
                >
                  {deactivating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserX className="w-4 h-4 mr-2" />}
                  Disattiva
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Anonymize dialog (GDPR) */}
      <Dialog open={!!anonymizeUser} onOpenChange={(open) => { if (!open) { setAnonymizeUser(null); setAnonymizeConfirmation(''); } }}>
        <DialogContent data-testid="anonymize-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-purple-600" />
              Anonimizzazione GDPR
            </DialogTitle>
          </DialogHeader>
          {anonymizeUser && (
            <div className="space-y-4 pt-2">
              <div className="text-sm text-muted-foreground">
                Stai per anonimizzare <b className="text-foreground">{anonymizeUser.name}</b> ({anonymizeUser.email}).
              </div>
              <div className="rounded-md bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-3 text-xs text-purple-900 dark:text-purple-100 space-y-1">
                <div><b>Operazione IRREVERSIBILE.</b> Da usare solo su richiesta esplicita GDPR dell&apos;ex dipendente.</div>
                <div>✓ Nome → <code>Ex Dipendente #{String(anonymizeUser.id).slice(-6)}</code></div>
                <div>✓ Email azzerata, password cancellata</div>
                <div>✓ ID conservato: <b>lo storico ticket/cartellini resta consistente</b></div>
                <div>✗ Non sarà più riattivabile né potrà essere ripristinato</div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="anon-confirm">Digita <code className="text-purple-700 dark:text-purple-300 font-bold">ANONIMIZZA</code> per confermare</Label>
                <Input
                  id="anon-confirm"
                  value={anonymizeConfirmation}
                  onChange={(e) => setAnonymizeConfirmation(e.target.value)}
                  placeholder="ANONIMIZZA"
                  data-testid="anonymize-confirmation-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setAnonymizeUser(null)} disabled={anonymizing}>
                  Annulla
                </Button>
                <Button
                  onClick={submitAnonymize}
                  disabled={anonymizing || anonymizeConfirmation !== 'ANONIMIZZA'}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="confirm-anonymize-button"
                >
                  {anonymizing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
                  Anonimizza (GDPR)
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
